import { UserStore } from "@Front/Stores/UserStore";
import { CustomerStore } from "@Front/Stores/CustomerStore";
import { PowerIcon } from "@heroicons/react/24/outline";
import { useCallback, useState, useEffect } from "react";
import { useRolePermissionContext, type RolePermissionContext } from "@Front/Stores/RolePermissionsStore";

import MenuItem from "../Menu/MenuItem";
import Auth from "@Front/Api/Auth/Customer/Auth";
import Typography, { ETypo, ETypoColor } from "../Typography";
import classes from "./classes.module.scss";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { isCustomer as checkIsCustomer, isThirdParty as checkIsThirdParty } from "le-coffre-resources/dist/MemberTypes";
import { reportIdNotRemoteLogoutSkipped } from "@Front/Api/Auth/IdNot/helpers/idNotRemoteLogoutSkipped";
import { resolveBrowserPublicFrontBaseUrl } from "@Front/Api/Auth/IdNot/helpers/idNotOAuthPublicBaseUrl";

function determineUserType(permissionContext: RolePermissionContext): {
	isCustomer: boolean;
	isThirdParty: boolean;
	isNotary: boolean;
} {
	const resolvedRole =
		permissionContext?.userType ??
		permissionContext?.globalRole ??
		sessionStorage.getItem("last_known_role") ??
		"";
	const normalizedRole = resolvedRole.toLowerCase();
	const isCustomer = checkIsCustomer(normalizedRole);
	const isThirdParty = checkIsThirdParty(resolvedRole);
	const isNotary = !isCustomer && !isThirdParty;
	return { isCustomer, isThirdParty, isNotary };
}

async function revokeLeCoffreToken(): Promise<void> {
	try {
		await Auth.getInstance().logout();
	} catch (error) {
		LoggerService.error("❌ Erreur lors de la révocation du token LeCoffre", { error });
	}
}

async function revokeIdNotCookies(): Promise<void> {
	await reportIdNotRemoteLogoutSkipped("Déconnexion notaire");
}

async function clearLocalSessions(isCustomer: boolean): Promise<void> {
	if (isCustomer) {
		await CustomerStore.instance.disconnect();
	} else {
		await UserStore.instance.disconnect();
	}
}

function clearCookiesAndStorage(): void {
	document.cookie.split(";").forEach((cookie) => {
		document.cookie = cookie.replace(/^ +/, "").replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
	});
	localStorage.clear();
}

function getRedirectUrl(isThirdParty: boolean): string {
	const base = resolveBrowserPublicFrontBaseUrl();
	if (isThirdParty) {
		const redirectUrl = `${base}/third-party/login`;
		return redirectUrl;
	}
	const redirectUrl = base ? `${base}/` : "/";
	return redirectUrl;
}

function useLogoutHandler(
	isLoggingOut: boolean,
	permissionContext: RolePermissionContext,
	setIsLoggingOut: React.Dispatch<React.SetStateAction<boolean>>,
): (() => Promise<void>) {
	return useCallback(async (): Promise<void> => {
		if (isLoggingOut) return;

		setIsLoggingOut(true);
		const { isCustomer, isThirdParty, isNotary } = determineUserType(permissionContext);

		await revokeLeCoffreToken();

		if (isNotary) {
			await revokeIdNotCookies();
		}

		if (isThirdParty) {
			sessionStorage.setItem("was_third_party", "true");
		}

		await clearLocalSessions(isCustomer);
		clearCookiesAndStorage();

		const redirectUrl = getRedirectUrl(isThirdParty);
		window.location.href = redirectUrl;
	}, [isLoggingOut, permissionContext, setIsLoggingOut]);
}

type IProps = {
	variant?: "menuItem" | "button";
};

export default function LogOut({ variant = "menuItem" }: IProps): React.JSX.Element {
	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const permissionContext = useRolePermissionContext();

	useEffect(() => {
		const roleHint = permissionContext?.userType ?? permissionContext?.globalRole;
		if (roleHint) {
			sessionStorage.setItem("last_known_role", roleHint);
		}
	}, [permissionContext?.userType, permissionContext?.globalRole]);

	const disconnect = useLogoutHandler(isLoggingOut, permissionContext, setIsLoggingOut);

	if (variant === "button") {
		return (
			<button className={classes["logout-button"]} onClick={disconnect} disabled={isLoggingOut}>
				<PowerIcon height="20px" width="20px" />
				<Typography typo={ETypo.TEXT_MD_SEMIBOLD} color={ETypoColor.COLOR_GENERIC_WHITE}>
					{isLoggingOut ? "Déconnexion..." : "Déconnexion"}
				</Typography>
			</button>
		);
	}

	return <MenuItem item={{ text: "Déconnexion", icon: <PowerIcon />, onClick: disconnect }} />;
}
